import {Injectable} from '@angular/core';
import {ICanOptions} from './auth.types';
import {ItemService} from './item.service';
import {AuthService} from './auth.service';

type PROCUREMENT_PROCESSING_BODY_TYPE = 'npa' | 'pe' | 'none';

@Injectable()
export class ContextAccessService {

    constructor(private authService: AuthService,
                public itemService: ItemService) {
    }

    /**
     * @param identifier -
     * @param type - Can be either `context_operation`, `context_operation_limitation`, and so on.
     * @param values - The set of actions to validate permissions against. e.g. ['enter', 'save', ...]
     * @param options -
     * @param exception - An optional expression which serves as an exception and is evaluated with `isTenderContextOperationAssigned`
     * and `isContextOperationAvailable` to take effect.
     */
    can(identifier: string, type: 'context_operation' | 'context_operation_limitation', values: string[], options?: ICanOptions,
        exception?: any): boolean {
        let tenderData = this.itemService.info.getValue().data;
        // if (!tenderData && (options === undefined || options === null)) {
        //     throw new Error('ContextAccessService [Error]: Neither `options` nor `tenderData` specified`');
        // }
        if ((options === undefined || options === null) && tenderData) {
            options = {
                tender_revision_id: tenderData.tender_revision_id,
                processing_body: tenderData.procurement_processing_body_slug,
                awarding_body: tenderData.awarding_body_slug,
                procurement_entity_slug: tenderData.procurement_entity_slugs
            } as ICanOptions;
        } else if (!tenderData && options) {
            tenderData = {};
            tenderData.procurement_processing_body_slug = options.processing_body;
            tenderData.awarding_body_slug = options.awarding_body;
        }
        const userContext = this.authService.CONTEXT as { context_operation: any[], context_operation_limitation: any[], tender_revision_ids: number[] };
        const userProcurementProcessingBody = this.authService.loggedInUser &&
        this.authService.loggedInUser.sub_party_slug === 'national-procurement-authority' ? 'npa' : 'pe' as PROCUREMENT_PROCESSING_BODY_TYPE;
        let isTenderContextOperationAssigned = false as boolean;
        let isContextOperationAvailable = false as boolean;

        if (type === 'context_operation' && userContext && userContext.context_operation) {
            for (const element of userContext.context_operation) {
                if (element.identifier === identifier) {
                    for (const value of values) {
                        if (element.values.findIndex(operation => operation === value) >= 0 && (value === 'enter' || value === 'view')) {
                            return true;
                        }
                    }
                }
                if (options &&
                    element.identifier === identifier &&
                    userContext.tender_revision_ids.indexOf(options.tender_revision_id) !== -1
                ) {
                    for (const value of values) {
                        if (element.values.findIndex(operation => operation === value) !== -1) {
                            isTenderContextOperationAssigned = true;
                            break;
                        }
                    }
                }
                if (element.identifier === identifier && element.type === type) {
                    for (const value of values) {
                        if ((element.values.findIndex(operation => operation === value) !== -1)) {
                            isContextOperationAvailable = true;
                            break;
                        }
                    }
                }
            }
            if (isContextOperationAvailable && isTenderContextOperationAssigned && exception !== undefined) {
                return !!exception;
            }

            if (isContextOperationAvailable) {
                if (this.authService.isPublicUser ||
                    this.authService.loggedInUser.party_slug === 'vendor' ||
                    this.authService.loggedInUser.party_slug === 'public') {
                    return isContextOperationAvailable;
                } else if (this.authService.loggedInUser.party_slug === 'default' || isTenderContextOperationAssigned) {
                    if (!tenderData || (!tenderData.awarding_body_slug && !tenderData.procurement_processing_body_slug)) {
                        return false;
                    }
                    const hasOperationLimitationAndAuthorized = this.isContextOperationAllowedPerLimitation(
                        identifier,
                        values,
                        userProcurementProcessingBody,
                        userContext,
                        tenderData
                    );
                    if (hasOperationLimitationAndAuthorized) {
                        return true;
                    } else {
                        if (userProcurementProcessingBody === 'pe') {
                            return options.procurement_entity_slug === this.authService.loggedInUser.sub_party_slug
                                && (this.authService.loggedInUser.party_slug === 'default' || options.processing_body === 'pe');
                        } else if (userProcurementProcessingBody === 'npa') {
                            return options.processing_body === 'npa' ||
                                (options.processing_body === 'pe'
                                    && options.procurement_entity_slug === 'national-procurement-authority');
                        } else {
                            return false;
                        }
                    }
                }
            }
        }
        return false;
    }

    isContextOperationAllowedPerLimitation(identifier: string,
                                           actions: string[],
                                           userProcurementProcessingBody: string,
                                           userContext: any,
                                           tenderData: any) {
        if (userContext.context_operation_limitation) {
            for (const limitation of userContext.context_operation_limitation) {
                for (const action of actions) {
                    if (limitation.identifier === (identifier + '_' + action)) {
                        for (const condition of limitation.values) {
                            if (tenderData
                                && tenderData[((condition as string).split('::')[1] as string).replace(/-/g, '_')]
                                === userProcurementProcessingBody) {
                                return true;
                            }
                        }
                    }
                }
            }
        }
        return false;
    }

    isTenderContextOperationAssigned(sourceIdentifier: string,
                                     targetIdentifier: string,
                                     sourceType: string,
                                     targetType: string,
                                     values: string[],
                                     identifierValue: string,
                                     sourceTenderRevisionId: number,
                                     targetTenderRevisionId: number): boolean {
        if (sourceIdentifier === targetIdentifier &&
            sourceType === targetType &&
            values.findIndex(valueItem => valueItem === identifierValue) >= 0 &&
            sourceTenderRevisionId === targetTenderRevisionId) {
            return true;
        } else {
            return false;
        }
    }
}
